// pages/secure/RetentionPage.js
// Records retention compliance (68 IAC 1450.755): a single register of every
// record-bearing store, its counts and retention windows, plus legal holds.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from '../../components/secure/Modal';

const CLASS_LABEL = {
    transaction: 'Transaction records', escrow: 'Escrow records', employment: 'Employment / agreements',
    communication: 'Communications', reference: 'Reference', other: 'Other',
};

const RetentionPage = () => {
    const apiClient = useApiClient();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [holdForm, setHoldForm] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const load = useCallback(() => {
        apiClient.get('/api/retention/dashboard').then(setData).catch(err => setError(err.message));
    }, [apiClient]);
    useEffect(() => { load(); }, [load]);

    const placeHold = async () => {
        setIsSaving(true); setError('');
        try {
            await apiClient.post('/api/retention/holds', holdForm);
            setHoldForm(null);
            load();
        } catch (err) { setError(err.message); }
        finally { setIsSaving(false); }
    };

    const release = async (h) => {
        if (!window.confirm(`Release the hold "${h.reason}"?`)) return;
        try { await apiClient.post(`/api/retention/holds/${h.hold_id}/release`); load(); }
        catch (err) { setError(err.message); }
    };

    if (error) return <div className="p-6"><p className="text-red-500">{error}</p></div>;
    if (!data) return <div className="p-6"><p>Building the records register...</p></div>;

    // Group sources by record class
    const byClass = {};
    data.sources.forEach(s => { (byClass[s.class] = byClass[s.class] || []).push(s); });

    return (
        <div className="p-6 max-w-5xl">
            <div className="flex items-center justify-between mb-1">
                <h1 className="text-2xl font-bold text-slate-800">Records Retention</h1>
                <button onClick={() => setHoldForm({ scope: 'transaction', scope_ref: '', reason: '' })}
                        className="bg-slate-700 text-white px-4 py-2 rounded-md hover:bg-slate-800">+ Legal hold</button>
            </div>
            <p className="text-slate-500 mb-6">
                Illinois requires {data.policy.retention_years}-year retention of all records; the most recent {data.policy.onsite_years} years
                must be produced within {data.policy.production_window_hours} hours ({data.policy.rule}).
            </p>

            {/* Summary tiles */}
            <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-4 text-center">
                    <p className="text-xs uppercase text-slate-500">Records on file</p>
                    <p className="text-2xl font-bold text-slate-700">{data.total_records.toLocaleString()}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-4 text-center">
                    <p className="text-xs uppercase text-slate-500">Past 5-yr window</p>
                    <p className="text-2xl font-bold text-slate-500">{data.purge_eligible.toLocaleString()}</p>
                    <p className="text-xs text-slate-400">purge-eligible (if no hold)</p>
                </div>
                <div className="bg-white rounded-lg shadow p-4 text-center">
                    <p className="text-xs uppercase text-slate-500">Active legal holds</p>
                    <p className={`text-2xl font-bold ${data.holds_active ? 'text-amber-600' : 'text-slate-700'}`}>{data.holds_active}</p>
                </div>
            </div>

            {/* Register by class */}
            {Object.entries(byClass).map(([cls, sources]) => (
                <div key={cls} className="mb-6">
                    <h2 className="text-lg font-semibold text-slate-700 mb-2">{CLASS_LABEL[cls] || cls}</h2>
                    <div className="bg-white rounded-lg shadow overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                                <tr><th className="text-left p-3">Record store</th><th className="text-right p-3">Total</th>
                                    <th className="text-right p-3">Within 2-yr (on-site)</th><th className="text-right p-3">Past 5-yr</th>
                                    <th className="text-left p-3">Oldest</th><th className="text-left p-3">Newest</th></tr>
                            </thead>
                            <tbody className="divide-y">
                                {sources.map(s => (
                                    <tr key={s.key}>
                                        <td className="p-3 text-slate-700">{s.label}{s.unavailable && <span className="text-xs text-slate-400"> (n/a)</span>}</td>
                                        <td className="p-3 text-right font-medium">{(s.total || 0).toLocaleString()}</td>
                                        <td className="p-3 text-right text-green-600">{(s.onsite_window || 0).toLocaleString()}</td>
                                        <td className="p-3 text-right text-slate-400">{(s.purge_eligible || 0).toLocaleString()}</td>
                                        <td className="p-3 text-slate-500">{s.oldest ? new Date(s.oldest).toLocaleDateString() : '—'}</td>
                                        <td className="p-3 text-slate-500">{s.newest ? new Date(s.newest).toLocaleDateString() : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}

            {/* Active holds */}
            {data.holds.length > 0 && (
                <div className="mb-6">
                    <h2 className="text-lg font-semibold text-amber-700 mb-2">Active Legal Holds</h2>
                    <div className="bg-white rounded-lg shadow divide-y">
                        {data.holds.map(h => (
                            <div key={h.hold_id} className="p-3 flex items-center gap-3">
                                <div className="flex-1">
                                    <p className="font-medium text-slate-700">{h.reason}</p>
                                    <p className="text-xs text-slate-500">Scope: {h.scope}{h.scope_ref ? ` #${h.scope_ref}` : ' (all)'} · placed {new Date(h.placed_at).toLocaleDateString()}</p>
                                </div>
                                <button onClick={() => release(h)} className="text-blue-600 hover:underline text-sm">Release</button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <p className="text-xs text-slate-400">
                Records may be kept electronically with monthly backup. This brokerage's nightly database + uploads backup satisfies
                the backup requirement; older records (beyond 2 years) are producible within 30 days.
            </p>

            {/* Place-hold modal */}
            <Modal open={!!holdForm} onClose={() => setHoldForm(null)} title="Place a legal hold"
                   footer={<>
                       <button onClick={() => setHoldForm(null)} className="px-4 py-2 rounded-md border">Cancel</button>
                       <button onClick={placeHold} disabled={isSaving || !holdForm?.reason?.trim()}
                               className="bg-slate-700 text-white px-4 py-2 rounded-md hover:bg-slate-800 disabled:opacity-50">{isSaving ? 'Placing...' : 'Place hold'}</button>
                   </>}>
                {holdForm && (
                    <div className="space-y-3">
                        <p className="text-sm text-slate-500">A hold marks records as not purge-eligible even past the 5-year window (litigation, audit, Division inquiry).</p>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="block text-sm font-medium text-slate-700">Scope</label>
                                <select value={holdForm.scope} onChange={e => setHoldForm(p => ({ ...p, scope: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2">
                                    {['transaction', 'client', 'escrow', 'all'].map(s => <option key={s} value={s}>{s}</option>)}
                                </select></div>
                            <div><label className="block text-sm font-medium text-slate-700">Reference # (optional)</label>
                                <input value={holdForm.scope_ref} onChange={e => setHoldForm(p => ({ ...p, scope_ref: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2" /></div>
                        </div>
                        <div><label className="block text-sm font-medium text-slate-700">Reason</label>
                            <input value={holdForm.reason} onChange={e => setHoldForm(p => ({ ...p, reason: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2" placeholder="e.g. IDFPR inquiry #..." /></div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default RetentionPage;
